import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import cx from "classnames";
import { Button, Dropdown, Form, Image, InputGroup, Modal } from "react-bootstrap";
import Draggable from "react-draggable";

import { Property } from "../../components/properties/";
import { EditorContext } from "components/Editor";
import InfiniteScroll from "components/InfiniteScroll";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";

import {
  fetchVariables,
  createVariable,
  updateVariable,
  deleteVariable,
  invalidateVariableMappings,
} from "actions/variables";
import { updateToolsSettings } from "actions/editor";

import StoryboardContext from "components/Storyboard/context";
import { removeVariableMapping, updateVariableMappings } from "actions/pages";
import Portal from "components/Portal";
import { TEXT } from "constants/widgetTypes";

import styles from "./variables.module.scss";
import shortid from "shortid";
import { useMemo } from "react";
import { useCallback } from "react";

// Dropdown component with search functionality
const VariableDropdown = ({
  items,
  selectedItem,
  onSelect,
  isOpen,
  onToggle,
  onCreateNew,
  searchable = true,
  fieldType,
}) => {
  const dispatch = useDispatch();

  const variableList = items.filter((item) =>
    item.type.toLowerCase().includes(fieldType.toLowerCase())
  );
  const [searchTerm, setSearchTerm] = useState("");
  const storyboardContext = React.useContext(StoryboardContext);

  const filteredItems = React.useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return _.reduce(
      variableList,
      (acc, item) => {
        if (!searchTerm || item.name.toLowerCase().includes(lowerSearch)) {
          if (!item.userDefined) {
            acc.leadData.push(item);
          } else {
            acc.custom.push(item);
          }
        }
        return acc;
      },
      { leadData: [], custom: [] }
    );
  }, [searchTerm, variableList]);

  const dropdownRef = React.useRef(null);

  let iconClassName = "cleverstory-text-layer";

  // Determine additional class based on fieldType

  switch (fieldType) {
    case "COLOR":
      iconClassName = "cleverstory-font-color";
      break;

    case "TEXT":
      iconClassName = "cleverstory-text-layer";
      break;

    case "IMAGE":
      iconClassName = "cleverstory-background";
      break;

    case "VIDEO":
      iconClassName = "cleverstory-video";
      break;
  }

  // Clear search term when dropdown closes
  React.useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
    }
    
    if (isOpen) {
      // Delay to next tick to ensure DOM is ready
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 0);
    }
  }, [isOpen]);

  return (
    <Form.Group>
      <Form.Label
        htmlFor="dropdown-variable"
        className="d-flex align-items-center"
      >
        <div className={styles.selectVariableTextContainer}>
          <i className={iconClassName} />
          <span>{`Select ${_.capitalize(fieldType)} Variable`}</span>
        </div>
      </Form.Label>
      <Dropdown
        className={styles.filter}
        onToggle={onToggle}
        show={isOpen}
        autoClose="inside"
        drop="down"
        ref={dropdownRef}
        align="start"
      >
        <Dropdown.Toggle
          className={cx(styles.selected, { [styles.selectedActive]: isOpen })}
        >
          {selectedItem ? 
          <TruncatedTextWithTooltip content={selectedItem.name} maxWidth={230} placement='top'/>:
          "Select or create a variable"}
        </Dropdown.Toggle>

        <Dropdown.Menu
          className={styles.filterMenu}
          popperConfig={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 8], // controls vertical spacing (8px below the toggle)
                },
              },
              {
                name: "flip",
                options: {
                  fallbackPlacements: ["top", "bottom"], // only flip vertically
                },
              },
              {
                name: "preventOverflow",
                options: {
                  boundary: "viewport", // avoid escaping the screen
                  altAxis: false,
                  padding: 8,
                },
              },
            ],
            strategy: "fixed",
          }}
        >
          {searchable && (
            <InputGroup className={styles.searchBox}>
              <InputGroup.Text className={styles.icon}>
                <i className={cx("cleverstory-search")} />
              </InputGroup.Text>
              <Form.Control
                className={styles.inputField}
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </InputGroup>
          )}

          <Button
            variant="light"
            className={styles.dropdownButton}
            onClick={()=>{onCreateNew()}}
          >
            + Create Custom Variable
          </Button>

          {filteredItems.leadData.length > 0 ||
          filteredItems.custom.length > 0 ? (
            <>
              {filteredItems.custom.length > 0 && (
                <>
                  <span className={styles.dropdownSectionLabel}>CUSTOM</span>
                  {filteredItems.custom.map((item) => (
                    <Dropdown.Item
                      key={item.id}
                      onClick={() => onSelect(item)}
                      active={selectedItem?.id === item.id}
                    >
                      <TruncatedTextWithTooltip content={item.name} maxWidth={200} placement='top'/>
                    </Dropdown.Item>
                  ))}
                </>
              )}
              {filteredItems.leadData.length > 0 && (
                <>
                  <span className={styles.dropdownSectionLabel}>LEAD DATA</span>
                  {filteredItems.leadData.map((item) => (
                    <Dropdown.Item
                      key={item.id}
                      onClick={() => onSelect(item)}
                      active={selectedItem?.id === item.id}
                    >
                      <TruncatedTextWithTooltip content={item.name} maxWidth={250} placement='top'/>
                    </Dropdown.Item>
                  ))}
                </>
              )}
            </>
          ) : (
            <span className={styles.noResults}>
              {_.isEmpty(searchTerm) ? `No Variables` : `No matching variables found`}
            </span>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </Form.Group>
  );
};

// CreateVariableForm component
const CreateVariableForm = React.memo(
  ({ onSubmit, storyboardId, activeDropdown, value = null, editorSession }) => {
    const dispatch = useDispatch();

    const [state, setState] = useState({
      variableName: "",
      defaultValue: "",
    });

    const variablesUpdating = useSelector((state) =>
      _.get(state, "variables.updating", false)
    );

    const variablesCreating = useSelector((state) =>
      _.get(state, "variables.creating", false)
    );

    const handleSubmit = () => {
      const request = {
        name: state.variableName,
        defaultValue: state.defaultValue,
        type: activeDropdown?.type,
        storyboard: storyboardId,
      };

      const result = dispatch(createVariable(request));
      if (result) {
        onSubmit();
      }
    };

    const handleUpdate = () => {
      if (
        _.isEqual(state.variableName, value.name) &&
        _.isEqual(state.defaultValue, value.defaultValue)
      ) {
        return;
      }

      const request = {
        id: value?.id,
        name: state.variableName,
        defaultValue: state.defaultValue,
        type: value?.type,
        storyboard: storyboardId,
      };

      dispatch(
        updateVariable(request, editorSession, () => {
          dispatch(fetchVariables({ storyboard: storyboardId }, 0, 20));
          onSubmit();
        })
      );
    };

    const handleCancel = () => {
      if (!value) {
        onSubmit();
        return;
      }

      handleValueReset();
    };

    useEffect(() => {
      handleValueReset();
    }, [value]);

    const handleValueReset = () => {
      setState(() => {
        return {
          variableName: _.get(value, "name"),
          defaultValue: _.get(value, "defaultValue", "")?.toString() || "",
        };
      });
    };

    const handleInputChange = (e) => {
      const { name, value } = e.target;

      setState((state) => {
        return {
          ...state,
          [name]: value,
        };
      });
    };

    return (
      <div className={styles.createVariableContainer}>
        <div className={styles.createVariableNoteContainer}>
          <i className="cleverstory-help"></i>
          <span className={styles.createVariableNote}>
            Set the value for this custom variable when you configure it for distribution
          </span>
        </div>
        <div className={styles.createVariableForm}>
          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.formLabel}>
              <i className="cleverstory-text-layer" /> Enter variable name
            </Form.Label>
            <Form.Control
              type="text"
              disabled={value && !value?.userDefined}
              name="variableName"
              className={styles.formControl}
              placeholder="Name"
              value={state.variableName}
              onChange={handleInputChange}
            />
          </Form.Group>
          {(activeDropdown?.type == "TEXT" || value?.type === "TEXT") && (
            <Form.Group className={styles.formGroup}>
              <Form.Label className={styles.formLabel}>
                <i className="cleverstory-text-layer" /> Fall back Text
              </Form.Label>
              <Form.Control
                type="text"
                name="defaultValue"
                className={styles.formControl}
                placeholder="Default"
                value={state.defaultValue}
                onChange={handleInputChange}
              />
            </Form.Group>
          )}
        </div>

        <div className={styles.actions}>
          <Button
            className={styles.action}
            variant="secondary"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          {!value && (
            <Button
              className={styles.action}
              onClick={handleSubmit}
              variant="primary"
              disabled={
                !state.variableName ||
                !state.variableName.trim() ||
                variablesCreating
              }
            >
              {variablesCreating ? "Creating.." : "Create Variable"}
            </Button>
          )}
          {value && (
            <Button
              className={styles.action}
              onClick={handleUpdate}
              variant="primary"
              disabled={
                !state.variableName ||
                !state.variableName.trim() ||
                variablesUpdating
              }
            >
              {variablesUpdating ? "Updating.." : "Update Variable"}
            </Button>
          )}
        </div>
      </div>
    );
  }
);

// Single field component for when there's only one field
const SingleFieldVariable = ({
  field,
  items,
  selectedItem,
  onSelect,
  activeDropdown,
  handleToggle,
  handleCreateNewVariable,
}) => {
  return (
    <VariableDropdown
      items={items}
      selectedItem={selectedItem}
      onSelect={(item) => onSelect(item, field.name)}
      isOpen={activeDropdown?.name === field.name}
      onToggle={() => handleToggle(field)}
      onCreateNew={handleCreateNewVariable}
      fieldType={field.type}
    />
  );
};

// Main component
export const VariableProperty = React.memo(({
  widget,
  onCancel,
  fields,
  onConfirm,
}) => {
  const dispatch = useDispatch();
  const editorContext = React.useContext(EditorContext);
  const pageId = editorContext.editorSession.id.split("_")[1];

  const page = useSelector((state) =>
    _.get(state, `pages.page.${pageId}.item`, [])
  );
  const variableMappings = _.get(page, "variableMappings", []);
  const targetSelector = `#${widget.uid}`;

  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardId = useSelector((state) =>
    _.get(
      state,
      "storyboards.storyboard.item.id",
      _.get(storyboardContext, "id")
    )
  );

  const uid = widget.uid;
  const variablesData = useSelector((state) =>
    _.get(state, "variables.items", [])
  );

  // States
  const [view, setView] = useState("selection");
  const [items, setItems] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [selections, setSelections] = useState({});
  const [deleted, setDeleted]=useState([]);
  const [initialized, setInitialized]=useState(false);
  // Check if we only have one field
  const isSingleField = fields.length === 1;
  const singleField = isSingleField ? fields[0] : null;

  // Find widget mappings
  const widgetMappings = React.useMemo(() => {
    return variableMappings?.filter((mapping) => mapping.widget === uid) || [];
  }, [variableMappings, uid]);

  useEffect(() => {
    if (!initialized && widgetMappings.length && items.length) {
      handlePopulateMappings()
      setInitialized(true);
    }
  }, [widgetMappings, items, fields]);

  const handlePopulateMappings =()=>{
    const initialSelections = {};
  
    fields.forEach((field) => {
      const mapping = widgetMappings.find((m) => m.path === field.path);
      if (mapping) {
        const variable = items.find((item) => item.id === mapping.variable);
        if (variable) {
          initialSelections[field.name] = {...variable, uid: (mapping?.id || mapping?.uid)};
        }
      }
    });

    setSelections(initialSelections);
  }

  // Update items when variables are loaded
  useEffect(() => {
    if (variablesData && variablesData.length > 0) {
      setItems(variablesData);
    }
  }, [variablesData]);

  // Handlers
  const handleClose = () => {
    if (onCancel) {
      setInitialized(false);
      setDeleted([]);
      onCancel();
    }
  };

  const handleToggle = (field) => {
    if (_.isEqual(activeDropdown, field)) {
      setActiveDropdown(null);
      return;
    }
    setActiveDropdown(field);
  };

  const handleSelect = (item, fieldName) => {
    setSelections((prev) => ({
      ...prev,
      [fieldName]: {...item,  uid: prev[fieldName]?.uid || shortid()},
    }));
  };

  const handleCheckboxChange = (fieldName) => {
    setSelections((prev) => {
      const newSelections = { ...prev };
      if (fieldName in newSelections) {
        const uid = _.get(newSelections[fieldName], 'uid')
        if(uid){
          setDeleted((prevDeleted) =>
            prevDeleted.includes(uid)
              ? prevDeleted
              : [...prevDeleted, uid]
          );
        }
        delete newSelections[fieldName];
      } else {
        newSelections[fieldName] = null;
      }
      return newSelections;
    });
  };

  const handleCreateNewVariable = (type) => {
    setView("creation");
  };

  const handleBackToChooseView = () => {
    setView("selection");
  };

  const handleConfirm = () => {
    const mappingRequests = [];
    fields.forEach((field) => {
      if (selections[field.name]) {
        const existingMapping = widgetMappings.find(
          (m) => m.path === field.path
        );

        mappingRequests.push({
          id: existingMapping?.id,
          variable: selections[field.name].id,
          path: field.path,
          data: selections[field.name],
          uid: selections[field.name].uid,
          target: pageId,
          type: field.type,
          widget: uid,
        });
      }
    });

    if ((mappingRequests.length || deleted.length) && widget.type !== TEXT) {
      dispatch(updateVariableMappings(pageId, mappingRequests, deleted));
    } else if (mappingRequests.length && widget.type === TEXT) {
      onConfirm(_.head(mappingRequests));
    }

    handleClose();
  };


  const isSelectionValid = useCallback(() => {
    if (!isSingleField) {
      const validSelections = Object.entries(selections).filter(
        ([_, value]) => value && typeof value === 'object' && Object.values(value).some(Boolean)
      );
  
      const uniquePaths = _.uniqBy(widgetMappings, 'path');
  
      if (validSelections.length !== uniquePaths.length) return false;
  
      for (const [key, value] of validSelections) {
        const field = fields.find((f) => f.name === key);
        if (!field || !field.path) return false;
  
        const mapping = widgetMappings.find((m) => m.path === field.path);
        if (!mapping || mapping.variable !== value.id) return false;
      }
  
      return true;
    }
  
    // For single field mode
    const singleSelection = selections[singleField.name];
    return (
      singleSelection &&
      widgetMappings.length > 0 &&
      singleSelection.id === widgetMappings[0].variable
    );
  }, [selections, widgetMappings]);


  return (
    <Property.Dialog
      className={styles.styleDialog}
      width={300}
      target={targetSelector}
      onCancel={handleClose}
    >
      <Property.Header
        title={view == "selection" ? "Make Variable" : "Create Variable"}
        onBack={view == "creation" ? handleBackToChooseView : null}
        onCancel={handleClose}
      />
      <Property.Body>
        {view === "selection" && (
          <div className={styles.container}>
            <span className={styles.headerTitle}>
              Insert  a variable for this element to make it dynamic and personalized.
            </span>

            <div className={styles.variableContainer}>
              {isSingleField && (
                // Single field layout - keep checkbox but make it conditional
                <SingleFieldVariable
                  field={singleField}
                  items={items}
                  selectedItem={selections[singleField.name]}
                  onSelect={handleSelect}
                  activeDropdown={activeDropdown}
                  handleToggle={handleToggle}
                  handleCreateNewVariable={handleCreateNewVariable}
                />
              )}
              {!isSingleField && (
                <div className={styles.variableAccordion}>
                  {fields.map((field, index) => {
                    return (
                      <MultipleFieldVariable 
                        field={field} 
                        index={index}
                        selections={selections}
                        handleSelect={handleSelect}
                        handleCreateNewVariable={handleCreateNewVariable}
                        handleCheckboxChange={handleCheckboxChange}
                        items={items}
                        activeDropdown={activeDropdown}
                        handleToggle={handleToggle}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            <div className={styles.actions}>
              <Button
                size="sm"
                className={styles.action}
                variant="secondary"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                size="sm"
                disabled={isSelectionValid()}
                className={styles.action}
                variant="primary"
              >
                Save
              </Button>
            </div>
          </div>
        )}
        {view === "creation" && (
          <CreateVariableForm
            activeDropdown={activeDropdown}
            onSubmit={handleBackToChooseView}
            storyboardId={storyboardId}
          />
        )}
      </Property.Body>
    </Property.Dialog>
  );
});

const MultipleFieldVariable = React.memo(({field, items, index, selections, handleCheckboxChange, handleSelect, handleCreateNewVariable, activeDropdown, handleToggle})=>{

  const fieldName = field.name;
  const fieldType = _.get(field, "type", "TEXT");
  const isChecked = _.has(selections, fieldName);
  const selectedVariable = selections[fieldName];

  return (
    <div
    key={index}
    className={styles.customFormFieldContainer}
  >
    <Form.Check
      custom
      type="checkbox"
      label={fieldName}
      onMouseDown={() => {
        handleCheckboxChange(fieldName);
      }}
      checked={isChecked}
      className={styles.optionalCheck}
    />

    <div
      className={cx(styles.dropdownField, {
        [styles.viewDropDownField]: isChecked,
      })}
    >
      {isChecked && (
        <VariableDropdown
          items={items}
          fieldType={fieldType}
          selectedItem={selectedVariable}
          onSelect={(item) => handleSelect(item, fieldName)}
          isOpen={activeDropdown?.name === fieldName}
          onToggle={() => handleToggle(field)}
          onCreateNew={handleCreateNewVariable}
          fieldName={fieldName}
        />
      )}
    </div>
  </div>
  )
});

export const VariablesLayer = ({ root, style }) => {
  const dispatch = useDispatch();

  const variableTypes = [
    { value: "TEXT", label: "Text" },
    { value: "IMAGE", label: "Image" },
    { value: "COLOR", label: "Color" },
    { value: "VIDEO", label: "Video" },
  ];
  const editorContext = useContext(EditorContext);
  const pageId = editorContext.editorSession.id.split("_")[1];
  const storyboardContext = React.useContext(StoryboardContext);
  const editorSession = editorContext.editorSession.id;
  const loading = useSelector((state) =>
    _.get(state, "variables.loading", false)
  );
  const pagination = useSelector((state) =>
    _.get(state, "variables.pagination", {})
  );
  const hasMoreItems =
    _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;
  const variablesData = useSelector((state) =>
    _.get(state, "variables.items", [])
  );

  const [filter, setFilter] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [update, setUpdate] = useState(null);
  const [position, setPosition] = useState({
    x: 100,
    y: 300,
  });
  const [deleteModal, setDeleteModal] = useState({
    selected: null,
    show: false,
  });

  useEffect(() => {
    handleFetchVariables(0);
    calculateDialogPosition();
  }, []);

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    handleFetchVariables(nextPage);
  };

  const handleFetchVariables = (page = 0) => {
    if (storyboardContext) {
      var request = {
        storyboard: storyboardContext.id,
      };
      dispatch(fetchVariables(request, page, 20));
    }
  };

  const calculateDialogPosition = () => {
    var targetEl = document.querySelector(
      `#${editorSession} .editor_widget_scroll_container`
    );
    if (!targetEl) {
      return;
    }

    const rect = targetEl.getBoundingClientRect();

    const x = 40;
    const y = rect.top + 40;

    setPosition({
      x: x,
      y: y,
    });
  };

  const triggerDeleteModal = (variable) => {
    setDeleteModal((state) => {
      return {
        show: !state.show,
        selected: variable,
      };
    });
  };

  const handleDeleteVariable = () => {
    if (deleteModal.selected) {
      dispatch(deleteVariable(deleteModal.selected?.id, pageId, editorSession, ()=>{
        dispatch(invalidateVariableMappings())
      }));
    }
  };

  const handleSelect = (newFilter) => {
    setFilter(newFilter);
    setIsOpen(false);
  };

  const handleToggle = (isOpen) => {
    setIsOpen(isOpen);
  };

  const handlePositionChange = (e, ui) => {
    setPosition((position) => {
      return {
        x: position.x + ui.deltaX,
        y: position.y + +ui.deltaY,
      };
    });
  };

  const renderIcon = (variableType) => {
    switch (variableType) {
      case "TEXT":
        return "cleverstory-text-layer";

      case "COLOR":
        return "cleverstory-font-color";

      case "IMAGE":
        return "cleverstory-background";

      case "VIDEO":
        return "cleverstory-video";

      default:
        return "cleverstory-text-layer";
    }
  };

  const handleClose = () => {
    dispatch(
      updateToolsSettings(editorSession, {
        variables: false,
      })
    );
  };

  return (
    <Portal>
      <Draggable
        handle={`.${styles.dragHandle}`}
        bounds="parent"
        position={position}
        onDrag={handlePositionChange}
      >
        <div id="variableLayer" className={styles.variables} style={style}>
          <header className={styles.dragHandle}>
            <div className={styles.title}>Variables</div>
            <i className={cx(styles.draggabel, "cleverstory-drag")} />
            <div className={styles.actions}>
              <Button className={styles.close} onClick={handleClose}>
                <i className="cleverstory-close" />
              </Button>
            </div>
          </header>
          <Dropdown
            className={styles.filter}
            onToggle={handleToggle}
            show={isOpen}
          >
            <Dropdown.Toggle
              className={`${styles.selected} ${
                isOpen ? styles.selectedActive : ""
              }`}
            >
              {filter === null ? "All" : filter.label}
            </Dropdown.Toggle>

            <Dropdown.Menu className={styles.filterMenu}>
              <Dropdown.Item
                onClick={() => handleSelect(null)}
                active={filter === null}
              >
                All
              </Dropdown.Item>
              {_.map(variableTypes, (variable) => {
                return (
                  <Dropdown.Item
                    onClick={() => handleSelect(variable)}
                    active={filter === variable}
                  >
                    {variable.label}
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
          <InfiniteScroll
            className={cx(styles.scrollContainer)}
            loading={loading}
            hasMore={hasMoreItems}
            loadMore={loadMore}
          >
            {!_.isEmpty(variablesData) &&
              _.chain(variablesData)
                .filter((variable) => {
                  return (
                    !filter ||
                    _.toLower(variable.type) === _.toLower(filter.value)
                  );
                })
                .map((variablefield) => (
                  <div
                    className={styles.details}
                    onClick={() => {
                      setUpdate(variablefield);
                    }}
                    key={variablefield.id || variablefield.name}
                  >
                    <div className={styles.contentWrapper}>
                      <i
                        className={cx(
                          styles.icon,
                          renderIcon(_.get(variablefield, "type"))
                        )}
                      />
                      <span className={styles.name}>
                        <TruncatedTextWithTooltip
                          placement="top"
                          maxWidth={150}
                          content={_.get(variablefield, "name")}
                        >
                          {_.get(variablefield, "name")}
                        </TruncatedTextWithTooltip>
                      </span>
                    </div>
                    {variablefield.userDefined && (
                      <div className={cx(styles.actionContainer)}>
                        <Button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            triggerDeleteModal(variablefield);
                          }}
                          variant="info"
                        >
                          <i className="cleverstory-trash" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))
                .value()}
          </InfiniteScroll>
        </div>
      </Draggable>

      {deleteModal.show && (
        <DeleteVariableModal
          deleteModal={deleteModal.show}
          showDeleteModal={() => {
            triggerDeleteModal(null);
          }}
          variable={deleteModal.selected}
          onDelete={handleDeleteVariable}
        />
      )}

      {update && (
        <Property.Dialog
          className={styles.styleDialog}
          width={300}
          target={`#variableLayer`}
          onCancel={() => {
            setUpdate(null);
          }}
        >
          <Property.Header
            title="Update Variables"
            onCancel={() => {
              setUpdate(null);
            }}
          />
          <Property.Body>
            <CreateVariableForm
              editorSession={editorSession}
              storyboardId={storyboardContext.id}
              onSubmit={() => {
                setUpdate(null);
              }}
              value={update}
            />
          </Property.Body>
        </Property.Dialog>
      )}
    </Portal>
  );
};

export const DeleteVariableModal = ({
  deleteModal,
  showDeleteModal,
  onDelete,
  variable,
}) => {
  const deleting = useSelector((state) =>
    _.get(state, "variables.deleting", false)
  );
  const deleted = useSelector((state) =>
    _.get(state, "variables.deleted", false)
  );

  const handleDeleteClick = (e) => {
    onDelete(variable);
    e.stopPropagation();
  };

  useEffect(() => {
    if (deleted) {
      showDeleteModal();
    }
  }, [deleted]);

  return (
    <Modal
      className={styles.deletePageModal}
      contentClassName={styles.deletePageModalContent}
      dialogClassName={styles.deletePageModalDialog}
      backdropClassName={styles.backdropWrapper}
      show={deleteModal}
      centered
      size="lg"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Modal.Body className={styles.deletePageModalBody}>
        <div className={styles.detailsContainer}>
          <div className={styles.messageContainer}>
            <h2 className={styles.messageTitle}>Are you sure?</h2>
            <p className={styles.messageContent}>
              This action of yours will make the current Variable inactive. You
              can create new variable anytime from the editor.
            </p>
            <footer className={styles.deletePageModalButtons}>
              <Button
                variant="secondary"
                onClick={() => {
                  showDeleteModal();
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handleDeleteClick}>
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </footer>
          </div>
          <div>
            <Image
              src={"/images/deletePage.png"}
              className={styles.deletePageModalImage}
            />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
